import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

function Header({ ctaHref = "/booking" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeNav, setActiveNav] = useState("");

  const handleSectionNavigation = (event, targetId) => {
    event.preventDefault();
    setIsMenuOpen(false);
    setActiveNav(`section:${targetId}`);

    if (location.pathname === "/") {
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    navigate({ pathname: "/", hash: `#${targetId}` });
  };

  const isRouteCta =
    typeof ctaHref === "string" &&
    !ctaHref.startsWith("#") &&
    !ctaHref.startsWith("/#") &&
    !ctaHref.startsWith("mailto:");

  const sectionLinks = [
    { label: "Services", targetId: "services" },
    { label: "Industries", targetId: "industries" },
    { label: "Process", targetId: "process" }
  ];

  const routeLinks = [
    { label: "Design Concepts", to: "/design-concepts" },
    { label: "Blog", to: "/blog" }
  ];

  const toolLinks = [
    { label: "Web Intel", to: "/audit-tool" },
    { label: "Pixel Analytics", to: "/analytics-tool" }
  ];

  const ctaClassName = "inline-flex items-center justify-center rounded-full bg-blue-600 text-white font-medium transition hover:bg-blue-500";
  const desktopNavLinkClassName =
    "group relative inline-flex items-center justify-center pb-3 text-slate-700 transition hover:text-blue-600";

  useEffect(() => {
    if (location.hash) {
      setActiveNav(`section:${location.hash.replace("#", "")}`);
      return;
    }

    const matchingRoute = [...routeLinks, ...toolLinks].find((link) => link.to === location.pathname);
    if (matchingRoute) {
      setActiveNav(`route:${matchingRoute.to}`);
      return;
    }

    setActiveNav("");
  }, [location.hash, location.pathname]);

  const getDesktopNavLinkClassName = (isActive) =>
    `${desktopNavLinkClassName} ${isActive ? "text-blue-600" : ""}`;

  const getDesktopNavUnderlineClassName = (isActive) =>
    `pointer-events-none absolute bottom-0 left-1/2 h-[3px] -translate-x-1/2 rounded-full bg-blue-500 transition-all duration-300 ease-out ${
      isActive ? "w-full" : "w-0 group-hover:w-full"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 shadow-[0_10px_30px_rgba(15,23,42,0.06)] backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
        <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
            <span className="text-white text-sm font-semibold">N</span>
          </div>
          <span className="text-xl font-light tracking-tight">
            <span className="text-slate-950">Nucleon</span><span className="font-semibold text-blue-600">soft</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-10 px-2 py-2 text-[15px] font-medium text-slate-700">
          {sectionLinks.map((link) => {
            const isActive = activeNav === `section:${link.targetId}`;
            return (
              <a
                key={link.targetId}
                href={`/#${link.targetId}`}
                onClick={(event) => handleSectionNavigation(event, link.targetId)}
                className={getDesktopNavLinkClassName(isActive)}
              >
                {link.label}
                <span className={getDesktopNavUnderlineClassName(isActive)} />
              </a>
            );
          })}
          {routeLinks.map((link) => {
            const isActive = activeNav === `route:${link.to}`;
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setActiveNav(`route:${link.to}`)}
                className={getDesktopNavLinkClassName(isActive)}
              >
                {link.label}
                <span className={getDesktopNavUnderlineClassName(isActive)} />
              </Link>
            );
          })}
          {toolLinks.map((link) => {
            const isActive = activeNav === `route:${link.to}`;
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setActiveNav(`route:${link.to}`)}
                className={getDesktopNavLinkClassName(isActive)}
              >
              {link.label}
              <span className={getDesktopNavUnderlineClassName(isActive)} />
              </Link>
            );
          })}
        </div>

        {isRouteCta ? (
          <Link
            to={ctaHref}
            className={`${ctaClassName} hidden lg:inline-flex px-6 py-3 text-sm shadow-lg shadow-blue-100`}
          >
            Discovery Call
          </Link>
        ) : (
          <a
            href={ctaHref}
            className={`${ctaClassName} hidden lg:inline-flex px-6 py-3 text-sm shadow-lg shadow-blue-100`}
          >
            Discovery Call
          </a>
        )}

        <button
          type="button"
          onClick={() => setIsMenuOpen((current) => !current)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-slate-900 transition hover:border-blue-200 hover:text-blue-600 lg:hidden"
          aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {isMenuOpen ? (
        <div className="border-t border-slate-100 bg-white lg:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
            {sectionLinks.map((link) => (
              <a
                key={link.targetId}
                href={`/#${link.targetId}`}
                onClick={(event) => handleSectionNavigation(event, link.targetId)}
                className="rounded-xl px-3 py-3 text-base font-light text-slate-700 transition hover:bg-slate-50 hover:text-blue-600"
              >
                {link.label}
              </a>
            ))}
            {routeLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsMenuOpen(false)}
                className="rounded-xl px-3 py-3 text-base font-light text-slate-700 transition hover:bg-slate-50 hover:text-blue-600"
              >
                {link.label}
              </Link>
            ))}
            {toolLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsMenuOpen(false)}
                className="rounded-xl px-3 py-3 text-base font-light text-slate-700 transition hover:bg-slate-50 hover:text-blue-600"
              >
                {link.label}
              </Link>
            ))}
            {isRouteCta ? (
              <Link
                to={ctaHref}
                onClick={() => setIsMenuOpen(false)}
                className={`${ctaClassName} mt-3 w-full px-5 py-3 text-base`}
              >
                Discovery Call
              </Link>
            ) : (
              <a
                href={ctaHref}
                onClick={() => setIsMenuOpen(false)}
                className={`${ctaClassName} mt-3 w-full px-5 py-3 text-base`}
              >
                Discovery Call
              </a>
            )}
          </div>
        </div>
      ) : null}
    </header>
  );
}

export default Header;
